/**
 * @fileoverview Provides the `SignIn` component for user authentication. It handles email/password sign-in via `handleEmailSignIn` and social sign-in using `handleGitHubSignIn` and `handleGoogleSignIn`, leveraging the `useAuth` hook. The component manages form state and displays authentication errors.
 */

import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import '../../styles/components/Auth.css'
import { GitHubFilledIcon, GoogleIcon } from '../Icons'

export function SignIn() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { signInWithEmail, signInWithGitHub, signInWithGoogle, error } = useAuth()
  const navigate = useNavigate()

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !password) return

    setIsSubmitting(true)
    const success = await signInWithEmail(email, password)
    setIsSubmitting(false)

    if (success) {
      navigate('/dashboard') // Redirect to /account after successful login
    }
  }

  const handleGitHubSignIn = async () => {
    setIsSubmitting(true)
    const success = await signInWithGitHub()
    setIsSubmitting(false)

    if (success) {
      navigate('/dashboard')
    }
  }

  const handleGoogleSignIn = async () => {
    setIsSubmitting(true)
    const success = await signInWithGoogle()
    setIsSubmitting(false)

    if (success) {
      navigate('/dashboard')
    }
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <Link to="/" className="home-link">
          Ref.
        </Link>
        <h2 className="auth-title">Sign in</h2>

        {error && <div className="error-message">{error}</div>}

        <div className="social-auth-buttons">
          <button
            onClick={handleGitHubSignIn}
            className="cta-button secondary social-button"
            disabled={isSubmitting}
            aria-label="Sign in with GitHub"
          >
            <GitHubFilledIcon size={16} className="button-icon" />
            <span>Sign in with GitHub</span>
          </button>

          <button
            onClick={handleGoogleSignIn}
            className="cta-button secondary social-button"
            disabled={isSubmitting}
            aria-label="Sign in with Google"
          >
            <GoogleIcon size={16} className="button-icon" />
            <span>Sign in with Google</span>
          </button>
        </div>

        <div className="auth-divider">
          <span>OR</span>
        </div>

        <form onSubmit={handleEmailSignIn} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isSubmitting}
              placeholder="Enter your email"
              aria-label="Email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isSubmitting}
              placeholder="Enter your password"
              aria-label="Password"
            />
          </div>

          <button
            type="submit"
            className="cta-button primary"
            disabled={isSubmitting}
            aria-label="Sign in with Email"
          >
            {isSubmitting ? 'Signing in...' : 'Sign in with Email'}
          </button>
        </form>

        <div className="auth-links">
          <Link to="/auth/reset-password" className="link">
            Forgot Password?
          </Link>
          <span className="auth-separator">•</span>
          <Link to="/signup" className="link">
            Create an Account
          </Link>
        </div>
      </div>
    </div>
  )
}
